import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  SupportAgent as SupportAgentIcon,
  Close as CloseIcon,
  AddCircleOutline as AddIcon,
  FormatListBulleted as ListIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  AssignmentTurnedIn as ResolvedIcon,
  WarningAmber as AlertIcon,
  AccessTime as TimeIcon,
  Headphones as HeadsetChatIcon,
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5003";

const CATEGORIES = [
  { value: "Payroll Calculation", label: "Payroll & Salary Calculation" },
  { value: "Attendance & Time Tracking", label: "Attendance & Biometrics" },
  { value: "Leave Management", label: "Leave & Holiday Policy" },
  { value: "Performance & Appraisal", label: "Performance & Reviews" },
  { value: "Billing & Subscription", label: "Plan, Invoicing & Billing" },
  { value: "Bug / Technical Issue", label: "Bug / System Error" },
  { value: "General Inquiry", label: "General Feature Inquiry" },
  { value: "Other", label: "Other" },
];

const PRIORITIES = [
  { value: "Low", label: "Low (General query)" },
  { value: "Medium", label: "Medium (Standard issue)" },
  { value: "High", label: "High (Affecting daily work)" },
  { value: "Urgent", label: "Urgent (System blocker / SLA S1)" },
];

export default function FloatingSupportWidget() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Sync token state on auth changes or navigation
  useEffect(() => {
    const handleAuthChange = () => {
      setToken(localStorage.getItem("token"));
    };
    window.addEventListener("storage", handleAuthChange);
    window.addEventListener("authChange", handleAuthChange);
    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("authChange", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    setToken(localStorage.getItem("token"));
  }, [location.pathname]);

  // Form State
  const [formData, setFormData] = useState({
    category: "Payroll Calculation",
    priority: "Medium",
    subject: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // Tickets List State
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    if (isOpen && activeTab === 1) {
      fetchTickets();
    }
  }, [isOpen, activeTab]);

  const fetchTickets = async () => {
    setLoadingTickets(true);
    setTicketError("");
    try {
      const currentToken = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/helpdesk/tickets`, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        withCredentials: true,
      });
      if (res.data && res.data.success) {
        setTickets(res.data.data || []);
      }
    } catch (err) {
      setTicketError(err.response?.data?.message || "Failed to load support tickets from BCC.");
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!formData.subject.trim() || !formData.description.trim()) {
      setSubmitError("Please fill out both the Subject and Description.");
      return;
    }

    setSubmitting(true);
    try {
      const currentToken = token || localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/api/helpdesk/tickets`, formData, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        withCredentials: true,
      });

      if (res.data && res.data.success) {
        setSubmitSuccess(
          `Ticket ${res.data.data?.ticket_number || "created"} successfully forwarded to BCC Support!`
        );
        setFormData({
          category: "Payroll Calculation",
          priority: "Medium",
          subject: "",
          description: "",
        });
        // Switch to "My Tickets" tab after 1.5s
        setTimeout(() => {
          setActiveTab(1);
          setSubmitSuccess("");
        }, 1500);
      }
    } catch (err) {
      setSubmitError(
        err.response?.data?.message ||
          "Could not submit ticket to BCC. Please verify company registration."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status = "") => {
    const normalized = String(status).toUpperCase();
    switch (normalized) {
      case "NEW":
      case "OPEN":
        return <Chip size="small" label="Open / New" color="primary" variant="outlined" />;
      case "ASSIGNED":
        return <Chip size="small" label="Assigned" color="info" />;
      case "IN_PROGRESS":
        return <Chip size="small" label="In Progress" color="warning" />;
      case "WAITING_FOR_CUSTOMER":
        return <Chip size="small" label="Waiting for Feedback" color="secondary" />;
      case "RESOLVED":
        return (
          <Chip
            size="small"
            label="Resolved"
            color="success"
            icon={<CheckCircleIcon fontSize="small" />}
          />
        );
      case "CLOSED":
        return <Chip size="small" label="Closed" variant="outlined" />;
      default:
        return <Chip size="small" label={status || "Unknown"} />;
    }
  };

  const getPriorityColor = (priority = "") => {
    const p = String(priority).toUpperCase();
    if (p === "CRITICAL" || p === "URGENT") return "#ef4444";
    if (p === "HIGH") return "#f97316";
    if (p === "LOW") return "#10b981";
    return "#3b82f6";
  };

  const filteredTickets = tickets.filter((t) => {
    if (statusFilter === "ALL") return true;
    if (statusFilter === "OPEN")
      return !["RESOLVED", "CLOSED"].includes(String(t.status).toUpperCase());
    if (statusFilter === "RESOLVED")
      return ["RESOLVED", "CLOSED"].includes(String(t.status).toUpperCase());
    return true;
  });

  const publicPaths = ["/", "/login", "/register", "/terms", "/terms-of-use", "/privacy", "/privacy-policy", "/sso/callback"];
  const isPublicRoute = publicPaths.includes(location.pathname) || location.pathname.startsWith("/public/apply");

  if (!token || isPublicRoute) {
    return null;
  }

  return (
    <>
      {/* Floating Trigger Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
        }}
      >
        <Tooltip title="Help & Support Helpdesk" placement="left" arrow>
          <Fab
            variant="extended"
            onClick={() => setIsOpen(true)}
            sx={{
              background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
              color: "#ffffff",
              boxShadow: "0 10px 25px -5px rgba(79, 70, 229, 0.4)",
              fontWeight: 600,
              textTransform: "none",
              px: 2.5,
              py: 1,
              "&:hover": {
                background: "linear-gradient(135deg, #4338ca 0%, #2563eb 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 14px 28px -5px rgba(79, 70, 229, 0.5)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <SupportAgentIcon sx={{ mr: 1, fontSize: 22 }} />
            Help & Support
          </Fab>
        </Tooltip>
      </Box>

      {/* Support Center Dialog */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "85vh",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
        }}
      >
        {/* Modal Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            background: "linear-gradient(135deg, #0b1e3b 0%, #1e3a8a 55%, #1d4ed8 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                bgcolor: "rgba(255, 255, 255, 0.15)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <HeadsetChatIcon sx={{ color: "#93c5fd", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, color: "#ffffff" }}>
                PulseHR Customer Support
              </Typography>
              <Typography variant="caption" sx={{ color: "#bfdbfe" }}>
                Direct Helpdesk powered by BCC • 24/7 SLA Resolution
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsOpen(false)}
            sx={{
              color: "#bfdbfe",
              "&:hover": { color: "#ffffff", bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, bgcolor: "#f8fafc" }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ minHeight: 48 }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<AddIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Raise a Ticket"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              icon={<ListIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`My Tickets (${tickets.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* Modal Body */}
        <DialogContent sx={{ p: 3, bgcolor: "#ffffff" }}>
          {/* TAB 0: RAISE A TICKET */}
          {activeTab === 0 && (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                Describe your issue or query
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your ticket will be routed directly to the Central Support Team in BCC with an
                automated SLA timer.
              </Typography>

              {submitError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError("")}>
                  {submitError}
                </Alert>
              )}

              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {submitSuccess}
                </Alert>
              )}

              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="Issue Category"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    size="small"
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleFormChange}
                    size="small"
                  >
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <TextField
                  fullWidth
                  label="Subject / Summary"
                  name="subject"
                  value={formData.subject}
                  onChange={handleFormChange}
                  placeholder="e.g., PF deduction calculation mismatch for March payroll"
                  size="small"
                  required
                />

                <TextField
                  fullWidth
                  label="Detailed Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Provide details about the issue, affected employees or steps to reproduce..."
                  multiline
                  rows={5}
                  required
                />

                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <TimeIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                  <Typography variant="caption" color="#15803d" fontWeight={500}>
                    Standard SLA: Tickets receive initial review within 4 hours and target
                    resolution within 24 to 48 hours.
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsOpen(false)}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      bgcolor: "#4f46e5",
                      "&:hover": { bgcolor: "#4338ca" },
                      textTransform: "none",
                      borderRadius: 2,
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Submit Ticket"
                    )}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}

          {/* TAB 1: MY TICKETS */}
          {activeTab === 1 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Stack direction="row" spacing={1}>
                  {["ALL", "OPEN", "RESOLVED"].map((filter) => (
                    <Button
                      key={filter}
                      size="small"
                      variant={statusFilter === filter ? "contained" : "outlined"}
                      onClick={() => setStatusFilter(filter)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                      }}
                    >
                      {filter}
                    </Button>
                  ))}
                </Stack>

                <Tooltip title="Refresh tickets">
                  <IconButton
                    size="small"
                    onClick={fetchTickets}
                    disabled={loadingTickets}
                    sx={{ color: "text.secondary" }}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={{
                        animation: loadingTickets ? "spin 1s linear infinite" : "none",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              {ticketError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ticketError}
                </Alert>
              )}

              {loadingTickets ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={36} />
                </Box>
              ) : filteredTickets.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <SupportAgentIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>
                    No tickets found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You haven't raised any tickets yet, or none match the selected filter.
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setActiveTab(0)}
                    sx={{ mt: 1.5, textTransform: "none" }}
                  >
                    Raise a new ticket
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {filteredTickets.map((ticket) => (
                    <Card
                      key={ticket.id || ticket.ticket_number}
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                          borderColor: "#cbd5e1",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1.5,
                            mb: 1,
                          }}
                        >
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="caption" fontWeight={700} color="#4f46e5">
                                {ticket.ticket_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                • {new Date(ticket.created_at || Date.now()).toLocaleDateString()}
                              </Typography>
                            </Stack>
                            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                              {ticket.subject}
                            </Typography>
                          </Box>
                          <Box>{getStatusBadge(ticket.status)}</Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                        >
                          {ticket.description}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ mb: ticket.resolution_details ? 2 : 0 }}
                        >
                          <Chip
                            size="small"
                            label={ticket.category || "TECHNICAL"}
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 22 }}
                          />
                          <Chip
                            size="small"
                            label={`Priority: ${ticket.priority || "MEDIUM"}`}
                            sx={{
                              fontSize: "0.7rem",
                              height: 22,
                              bgcolor: `${getPriorityColor(ticket.priority)}15`,
                              color: getPriorityColor(ticket.priority),
                              fontWeight: 600,
                            }}
                          />
                          {ticket.sla_resolution_due_at && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <TimeIcon sx={{ fontSize: 13 }} />
                              SLA Due:{" "}
                              {new Date(ticket.sla_resolution_due_at).toLocaleDateString()}{" "}
                              {new Date(ticket.sla_resolution_due_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          )}
                        </Stack>

                        {/* Engineer Resolution Reply */}
                        {ticket.resolution_details && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 2,
                              bgcolor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              borderRadius: 2,
                              borderLeft: "4px solid #10b981",
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="#0f766e"
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Support Team Resolution:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {ticket.resolution_details}
                            </Typography>
                            {ticket.resolved_at && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Resolved on {new Date(ticket.resolved_at).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
